import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "drjp_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchInquiries = useCallback(async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/inquiries`, {
        headers: { "X-Admin-Token": t },
      });
      setInquiries(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Invalid admin token.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Failed to load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchInquiries(token);
  }, [token, fetchInquiries]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!tokenInput.trim()) {
      setError("Please enter the admin token.");
      return;
    }
    try {
      await axios.post(`${API}/admin/login`, { token: tokenInput });
      localStorage.setItem(TOKEN_KEY, tokenInput);
      setToken(tokenInput);
      setTokenInput("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid token.");
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInquiries([]);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setInquiries((prev) => prev.filter((i) => i.id !== id));
    } catch {
      setError("Could not delete.");
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (!token) {
    return (
      <div className="admin-page" data-testid="admin-login-page">
        <div className="admin-wrap">
          <div className="login-card">
            <h2>Admin Access</h2>
            <p className="hint">Enter your admin token to view consultancy inquiries.</p>
            <form onSubmit={onLogin}>
              <div className="form-group">
                <label htmlFor="admin-token">Admin Token</label>
                <input
                  type="password"
                  id="admin-token"
                  placeholder="Enter token"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  data-testid="admin-token-input"
                  autoFocus
                />
              </div>
              <button type="submit" className="btn-submit" data-testid="admin-login-btn">
                Unlock Dashboard
              </button>
              {error && <div className="form-error" data-testid="admin-error">{error}</div>}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <div className="admin-wrap">
        <div className="admin-header">
          <h1>Inquiries <span>· Dashboard</span></h1>
          <button className="admin-logout" onClick={onLogout} data-testid="admin-logout-btn">
            Logout
          </button>
        </div>

        <div className="inquiry-stats">
          <div className="stat-card">
            <div className="stat-label">Total Inquiries</div>
            <div className="stat-value" data-testid="stat-total">{inquiries.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Latest</div>
            <div className="stat-value" style={{ fontSize: "1rem" }}>
              {inquiries[0] ? fmt(inquiries[0].created_at) : "—"}
            </div>
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}

        {loading ? (
          <div className="empty-state"><h3>Loading…</h3></div>
        ) : inquiries.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <h3>No inquiries yet</h3>
            <p>New submissions from the contact form will appear here.</p>
          </div>
        ) : (
          <div className="inquiry-list" data-testid="inquiry-list">
            {inquiries.map((i) => (
              <div className="inquiry-card" key={i.id} data-testid={`inquiry-${i.id}`}>
                <div className="inquiry-top">
                  <div>
                    <div className="inquiry-name">{i.name}</div>
                    {i.organisation && <div className="inquiry-org">{i.organisation}</div>}
                  </div>
                  <div className="inquiry-meta">{fmt(i.created_at)}</div>
                </div>
                <div className="inquiry-area">{i.area}</div>
                <div className="inquiry-contact">
                  <span>
                    📧 <a href={`mailto:${i.email}`}>{i.email}</a>
                  </span>
                  {i.phone && (
                    <span>
                      📞 <a href={`tel:${i.phone}`}>{i.phone}</a>
                    </span>
                  )}
                </div>
                <div className="inquiry-message">{i.message}</div>
                <div className="inquiry-actions">
                  <button
                    className="btn-delete"
                    onClick={() => onDelete(i.id)}
                    data-testid={`delete-${i.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
